// What the Planning says when a bar is dropped on a room — the words only. The verdict itself is
// pms-core's `judgeRackDrop`; nothing here decides.

import type { DropVerdict, DropWarning } from '@lemontaigu/pms-core';
import { isoAddDays } from './logic.js';

const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/** Why a drop was refused, as the desk reads it. */
export function dropRefusalFr(v: Extract<DropVerdict, { ok: false }>, roomNumber: string): string {
  switch (v.reason) {
    case 'vega':
      return 'Cette réservation vient de Vega : Vega remettrait la chambre à sa place dans la demi-heure. À attribuer dans Vega.';
    case 'status':
      return 'Ce séjour est terminé ou annulé : on ne le déplace plus.';
    case 'sameRoom':
      return `Ce séjour est déjà dans la chambre ${roomNumber}.`;
    case 'otherProperty':
      return `La chambre ${roomNumber} est dans un autre établissement.`;
    case 'noCategory':
      return `La chambre ${roomNumber} n’a pas de catégorie : on ne sait pas la comparer à ce qui a été vendu.`;
    case 'busy':
      return `La chambre ${roomNumber} est occupée du ${dm(v.checkinDate)} au ${dm(v.checkoutDate)} : le séjour n’y tient pas.`;
  }
}

function warningFr(w: DropWarning, roomNumber: string, category: (id: number) => string): string {
  switch (w.kind) {
    case 'upgrade':
      return `Catégorie : vendu en ${category(w.sold)}, la chambre ${roomNumber} est en ${category(w.room)} — surclassement. Le tarif reste celui de ${category(w.sold)} ; la disponibilité suit la chambre.`;
    case 'below':
      return `Catégorie : vendu en ${category(w.sold)}, la chambre ${roomNumber} est en ${category(w.room)} — en dessous de ce qui a été vendu (déclassement). Le tarif est à régler avec le client.`;
    case 'dates':
      return `Dates : déposé sur le ${dm(w.dropStart)}, mais le séjour commence le ${dm(w.checkinDate)}. Les dates ne changent pas : du ${dm(w.checkinDate)} au ${dm(w.checkoutDate)}.`;
  }
}

/** The confirmation for a drop that is allowed but not clean; null when it is clean. */
export function dropConfirmFr(
  warnings: readonly DropWarning[],
  who: string,
  roomNumber: string,
  category: (id: number) => string,
): string | null {
  if (warnings.length === 0) return null;
  return [`Placer ${who} dans la chambre ${roomNumber} ?`, ...warnings.map((w) => `⚠ ${warningFr(w, roomNumber, category)}`)].join('\n\n');
}

/**
 * The night the stay's first night would land on. `cellIndex` is the day column under the pointer
 * when dropped; `grabbedDaysIn` is how many days after the stay's arrival the desk took hold of the
 * bar, so dropping on the same days gives the arrival date back whichever part of the bar was held.
 */
export function dropStartFor(days: readonly string[], cellIndex: number, grabbedDaysIn: number): string | null {
  const cell = days[cellIndex];
  return cell === undefined ? null : isoAddDays(cell, -grabbedDaysIn);
}
